// ============================================
// GraphicsConfig — configurable GPU constants
// ============================================
//
// Central source of truth for all rendering limits and quality settings.
// Values are set once at project load time (before pipelines are created)
// and passed to shader pipelines as WGSL `override` constants.
//
// Usage:
// ```ts
// import { graphicsConfig, setGraphicsConfig } from '@engine/render2d';
//
// // Read current values
// const res = graphicsConfig.shadowMapResolution;
//
// // Update from project settings (before renderer init)
// setGraphicsConfig({ shadowMapResolution: 1024, maxLightsPerTile: 128 });
// ```

// ============================================================================
// Types
// ============================================================================

/**
 * Render pipeline path.
 * - 'deferred' — GBuffer + deferred light accumulation (best quality, more GPU memory)
 * - 'forward+' — Forward rendering with tile-based light culling (faster, less memory)
 */
export type RenderPath = 'deferred' | 'forward+';

/**
 * Configurable graphics limits and quality settings.
 *
 * These values control GPU buffer sizes and shader `override` constants.
 * Changing them after pipeline creation requires a full renderer reinit.
 */
export interface GraphicsConfig {
  // ---- Render path ----

  /** Which render pipeline to use. 'deferred' for GBuffer, 'forward+' for direct lighting. */
  renderPath: RenderPath;

  // ---- Light buffer sizing ----

  /** Maximum number of lights in the storage buffer. */
  maxLights: number;

  // ---- Forward+ tile culling ----

  /** Tile size in pixels for Forward+ light culling (NxN). */
  tileSize: number;

  /** Maximum number of light indices stored per tile. */
  maxLightsPerTile: number;

  // ---- Shadow mapping ----

  /** Angular resolution of each 1D shadow map (samples per light). */
  shadowMapResolution: number;

  /** Maximum number of shadow-casting lights (rows in shadow atlas). */
  maxShadowLights: number;

  /** Maximum number of shadow caster edges in the edge buffer. */
  maxShadowEdges: number;

  /** Number of PCF taps for soft shadow sampling. */
  shadowPCFSamples: number;
}

// ============================================================================
// Defaults
// ============================================================================

export const DEFAULT_GRAPHICS_CONFIG: Readonly<GraphicsConfig> = {
  renderPath: 'deferred',
  maxLights: 1024,
  tileSize: 16,
  maxLightsPerTile: 64,
  shadowMapResolution: 512,
  maxShadowLights: 128,
  maxShadowEdges: 4096,
  shadowPCFSamples: 33,
};

// ============================================================================
// Singleton
// ============================================================================

/**
 * Current graphics configuration.
 * Read this directly for access to live values.
 */
export const graphicsConfig: GraphicsConfig = { ...DEFAULT_GRAPHICS_CONFIG };

/**
 * Update graphics configuration (partial merge).
 * Should be called before renderer/pipeline initialization.
 */
export function setGraphicsConfig(partial: Partial<GraphicsConfig>): void {
  Object.assign(graphicsConfig, partial);
}

// ============================================================================
// Helpers — shader override constant records
// ============================================================================

/** Override constants for shaders that use Forward+ tile culling. */
export function getTileOverrides(): Record<string, number> {
  return {
    TILE_SIZE: graphicsConfig.tileSize,
    MAX_LIGHTS_PER_TILE: graphicsConfig.maxLightsPerTile,
  };
}

/** Override constants for shaders that sample shadow maps. */
export function getShadowSamplingOverrides(): Record<string, number> {
  return {
    SHADOW_MAP_RES: graphicsConfig.shadowMapResolution,
    SHADOW_PCF_SAMPLES: graphicsConfig.shadowPCFSamples,
  };
}

/** Override constants for the shadow map compute shader. */
export function getShadowComputeOverrides(): Record<string, number> {
  return {
    SHADOW_MAP_RES: graphicsConfig.shadowMapResolution,
  };
}

/** All overrides for the deferred light accumulation pipeline. */
export function getLightAccumOverrides(): Record<string, number> {
  return {
    ...getTileOverrides(),
    ...getShadowSamplingOverrides(),
  };
}

/** All overrides for the forward+ sprite pipeline. */
export function getForwardPlusOverrides(): Record<string, number> {
  return {
    ...getTileOverrides(),
    ...getShadowSamplingOverrides(),
  };
}
